import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { storage } from '../utils/storage';
import { LevelData } from '../types';
import { useGame } from '../context/GameContext';

export const ResultsDashboard: React.FC = () => {
  const { state } = useGame();
  const data = storage.getSessionData(state.sessionId);

  const exportData = (format: 'csv' | 'json') => {
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `session-${state.sessionId}-${timestamp}.${format}`;
    const content = format === 'csv' 
      ? storage.exportToCSV(state.sessionId)
      : storage.exportToJSON(state.sessionId);
    storage.downloadFile(content, filename);
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow-md space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-800">Session Results</h2>
        <div className="space-x-4">
          <button
            onClick={() => exportData('csv')}
            className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            Export CSV
          </button>
          <button
            onClick={() => exportData('json')}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Export JSON
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Level</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time (s)</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Accuracy</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Errors</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Score</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map((level: LevelData) => (
              <tr key={level.level}>
                <td className="px-6 py-4 whitespace-nowrap">{level.level}</td>
                <td className="px-6 py-4 whitespace-nowrap">{level.completionTimeSec.toFixed(1)}</td>
                <td className="px-6 py-4 whitespace-nowrap">{level.accuracyPct.toFixed(1)}%</td>
                <td className="px-6 py-4 whitespace-nowrap">{level.errors}</td>
                <td className="px-6 py-4 whitespace-nowrap">{level.score.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h3 className="text-lg font-semibold mb-4">Performance Over Time</h3>
          <LineChart width={400} height={300} data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="level" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="accuracyPct" name="Accuracy %" stroke="#82ca9d" />
            <Line type="monotone" dataKey="completionTimeSec" name="Time (s)" stroke="#8884d8" />
          </LineChart>
        </div>

        <div>
          <h3 className="text-lg font-semibold mb-4">Errors and Difficulty</h3>
          <LineChart width={400} height={300} data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="level" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="errors" name="Errors" stroke="#ff7300" />
            <Line type="monotone" dataKey="difficultyIndex" name="Difficulty" stroke="#0088fe" />
          </LineChart>
        </div>
      </div>
    </div>
  );
};